import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { StarIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/LoadingSpinner';
import { burialPlotBookingService } from '../../services/burial-plot-booking.service';
import { reviewService } from '../../services/review.service';
import { getBurialPlotPaymentStatus, formatDate } from '../../utils/formatters';
import ReviewForm from '../../components/reviews/ReviewForm';

const BurialPlotBookingDetailsPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [booking, setBooking] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<any>(null);
  const [isReviewFormOpen, setIsReviewFormOpen] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  useEffect(() => {
    if (!bookingId) {
      setError('Invalid booking ID');
      setIsLoading(false);
      return;
    }

    loadBookingDetails();
  }, [bookingId]);

  // Load review when booking is loaded
  useEffect(() => {
    if (booking && bookingId) {
      loadReview();
    }
  }, [booking, bookingId]);

  // Check if we've returned from payment page and refresh data
  useEffect(() => {
    if (location.state?.paymentCompleted) {
      // Refresh booking details after payment submission
      loadBookingDetails();

      // Clear the state to prevent repeated refreshes
      navigate(location.pathname, { replace: true, state: {} });

      // Show success message
      toast.success('Payment submitted successfully! Your payment is now pending verification.');
    }
  }, [location.state]);

  const loadBookingDetails = async () => {
    try {
      const response = await burialPlotBookingService.getBookingDetails(bookingId);
      console.log('🔍 DEBUG - API response:', response);

      if (response.success && response.data) {
        console.log('🔍 DEBUG - Booking details loaded:', response.data);
        console.log('🔍 DEBUG - Payment status analysis:', getBurialPlotPaymentStatus(response.data));
        setBooking(response.data);
      } else {
        throw new Error(response.message || 'Failed to load booking details');
      }
    } catch (error: any) {
      console.error('Failed to load booking details:', error);
      setError('Booking not found or access denied');
    } finally {
      setIsLoading(false);
    }
  };

  const loadReview = async () => {
    try {
      const existingReview = await reviewService.getReview(bookingId!, 'BURIAL_PLOT');
      if (existingReview) {
        setReview(existingReview);
      }
    } catch (error: any) {
      console.error('Failed to load review:', error);
      // Don't set error state for review loading failure
    }
  };

  const handleCompletePayment = () => {
    navigate(`/bookings/burial-plot/payment/${bookingId}`, {
      state: {
        booking: booking,
        pricing: booking.pricing,
        selectedPlot: booking.plot
      }
    });
  };

  const handleSubmitReview = async (rating: number, comment?: string) => {
    if (!bookingId) return;

    setIsSubmittingReview(true);
    try {
      const result = await reviewService.createReview(bookingId, {
        rating,
        comment
      });

      // Refresh the review after submission
      await loadReview();
      setIsReviewFormOpen(false);
      toast.success('Review submitted successfully!');
    } catch (error: any) {
      console.error('Submit review error:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to submit review');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 text-red-400 mb-4">
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Booking Not Found</h3>
          <p className="text-gray-500 mb-4">{error || 'Unable to load booking details'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const paymentStatus = getBurialPlotPaymentStatus(booking);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-blue-600 hover:text-blue-700 font-medium mb-4"
        >
          ← Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-neutral-900">
          Burial Plot Booking Details
        </h1>
        <p className="mt-2 text-neutral-600">
          Booking ID: {booking.id}
        </p>
      </div>

      {/* Status Alert */}
      <div className={`mb-6 p-4 rounded-lg border ${
        paymentStatus.isPaymentVerified
          ? 'bg-green-50 border-green-200 text-green-800'
          : paymentStatus.isPaymentSubmitted
          ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
          : 'bg-red-50 border-red-200 text-red-800'
      }`}>
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium">
              {paymentStatus.statusMessage}
            </h3>
            <p className="mt-1 text-sm">
              {paymentStatus.detailMessage}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plot Details */}
        <div className="bg-white rounded-lg border border-neutral-200">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Plot Details</h2>
            {booking.plot ? (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Plot Number:</span>
                  <span className="font-medium">{booking.plot.plotNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Section:</span>
                  <span className="font-medium">{booking.plot.section}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Row:</span>
                  <span className="font-medium">{booking.plot.row}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Size:</span>
                  <span className="font-medium">{booking.plot.plotSize}</span>
                </div>
                {booking.plot.dimensions && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dimensions:</span>
                    <span className="font-medium">{booking.plot.dimensions}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">Plot details not available</p>
            )}
          </div>
        </div>

        {/* Burial Details */}
        <div className="bg-white rounded-lg border border-neutral-200">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Burial Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Burial Size:</span>
                <span className="font-medium">{booking.burialSize}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cemetery:</span>
                <span className="font-medium">{booking.burialArea}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Urgency:</span>
                <span className="font-medium">{booking.urgencyLevel}</span>
              </div>
              {booking.scheduledDateTime && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Scheduled Date:</span>
                  <span className="font-medium">
                    {new Date(booking.scheduledDateTime).toLocaleDateString()}
                  </span>
                </div>
              )}
              {booking.transferReference && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Transfer Reference:</span>
                  <span className="font-medium">{booking.transferReference}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Information */}
      <div className="mt-6 bg-white rounded-lg border border-neutral-200">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Payment Information</h2>
          {booking.pricing ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Base Price:</span>
                <span className="font-medium">RM {booking.pricing.basePrice}</span>
              </div>
              {booking.pricing.urgencySurcharge > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Urgency Surcharge:</span>
                  <span className="font-medium">RM {booking.pricing.urgencySurcharge}</span>
                </div>
              )}
              <div className="border-t pt-3">
                <div className="flex justify-between">
                  <span className="font-semibold">Total Amount:</span>
                  <span className="font-bold text-lg">RM {booking.pricing.total}</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Payment information not available</p>
          )}
        </div>
      </div>

      {/* Actions */}
      {paymentStatus.actionRequired && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={handleCompletePayment}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Complete Payment
          </button>
        </div>
      )}

      {/* Review Section */}
      {booking.status === 'COMPLETED' && (
        <div className="mt-6">
          {review ? (
            <div className="bg-white rounded-lg border border-neutral-200">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-neutral-900 mb-4">Your Review</h2>
                <div className="flex items-center space-x-2 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      className={`h-5 w-5 ${
                        star <= review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                      }`}
                    />
                  ))}
                  <span className="text-sm text-gray-600 ml-2">
                    {review.rating}/5
                  </span>
                </div>
                {review.comment && (
                  <p className="text-gray-700 text-sm">{review.comment}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Reviewed on {formatDate(review.createdAt)}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <button
                onClick={() => setIsReviewFormOpen(true)}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                <StarIcon className="h-5 w-5 mr-2" />
                Rate Service
              </button>
            </div>
          )}
        </div>
      )}

      {/* Review Form Modal */}
      {isReviewFormOpen && (
        <ReviewForm
          isOpen={isReviewFormOpen}
          onClose={() => setIsReviewFormOpen(false)}
          onSubmit={handleSubmitReview}
          isSubmitting={isSubmittingReview}
          providerName={`Burial Plot Service - ${booking.burialArea}`}
        />
      )}
    </div>
  );
};

export default BurialPlotBookingDetailsPage;